import type { AutoWidgetRegistry, WidgetOptions, WidgetInstance, VoiceStatus, FashionTryOnModal, PendingConfirmation, ExistingWidgetSessionCredential, WidgetAction, InsuranceIntakeResolution, WidgetMessage, RuntimeChatResponse, WidgetVoiceConfig, WidgetStylePreset, AgentPlatformWidgetApi } from "./types";
import { scriptTheme, widgetStylePreset, scriptShopperContext, ensureSessionId, loadTranscript, renderWidgetTitle, sotmResetIcon, sotmCloseIcon, configPrimaryColor, colorValue, sotmSignalIcon, renderFashionSettings, renderFashionTryOnModal, renderPendingConfirmation, sotmSendIcon, normalizeActions, renderWidgetMessage, renderTypingIndicator, renderInsuranceWelcome, renderSotmWelcome, resolveInsuranceIntakeInput, saveTranscript, normalizeSources, sourcesFromToolContext, nextSessionId, sessionStorageKey, pendingConfirmationFromElement, dispatchActionEvent, insuranceLocationDisplay, dispatchActionDetail, pendingActionEvent } from "./rendering";
import { defaultRuntimeUrl, fallbackConfig, responseMessage, shopperContextPayload, loadWidgetConfig } from "./transport";
import { normalizedVoiceConfig, isVoiceAvailable, voiceStatusLabel, voiceUnavailableLabel, sampleRateFromElevenLabsFormat, vapiTranscript, normalizeVoiceStatus, floatToPcm16, voiceProviderStatusError, base64ToBytes } from "./voice";
import { attr, trimTrailingSlash, escapeHtml, escapeAttribute, required, parseRecord, stringValue, publicVisitorText, publicVisitorSpeech, nonEmptyRecord, safeUrl, stringArray, numberValue, formatPrice } from "./formatting";

(function () {
  const widgetFontBase = document.currentScript instanceof HTMLScriptElement && document.currentScript.src
    ? new URL("fonts/ui/", document.currentScript.src).href : null;

  const widgetAttribute = "data-agent-platform-widget-mounted";
  const widgetHostAttribute = "data-agent-platform-widget-host";
  const widgetRegistrySymbol = Symbol.for("agent-platform.widget.instances");

  const preconnectedRuntimeOrigins = new Set<string>();

  function autoWidgetRegistry(): AutoWidgetRegistry {
    const scope = window as unknown as Record<PropertyKey, unknown>;
    const existing = scope[widgetRegistrySymbol];
    if (existing instanceof Map) {
      return existing as AutoWidgetRegistry;
    }
    const registry: AutoWidgetRegistry = new Map();
    scope[widgetRegistrySymbol] = registry;
    return registry;
  }

  function autoWidgetKey(options: WidgetOptions): string {
    return `${options.customerId}:${options.appId}`;
  }

  function autoWidgetFingerprint(options: WidgetOptions): string {
    return JSON.stringify({
      appId: options.appId,
      customerId: options.customerId,
      runtimeKey: options.runtimeKey,
      runtimeUrl: trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl),
      sessionId: options.sessionId ?? "",
      title: options.title ?? "",
      launcherLabel: options.launcherLabel ?? "",
      inputPlaceholder: options.inputPlaceholder ?? "",
      position: options.position ?? "right",
      theme: options.theme ?? "light",
      primaryColor: options.primaryColor ?? "",
      stylePreset: options.stylePreset ?? "default",
      shopperContext: options.shopperContext ?? {}
    });
  }

  function autoMountWidget(options: WidgetOptions, script?: HTMLScriptElement): WidgetInstance {
    const registry = autoWidgetRegistry();
    const key = autoWidgetKey(options);
    const fingerprint = autoWidgetFingerprint(options);
    const existing = registry.get(key);

    if (existing?.instance.host.isConnected && existing.fingerprint === fingerprint) {
      script?.setAttribute("data-agent-platform-widget-reused", "true");
      window.dispatchEvent(new CustomEvent("agent-platform-widget:duplicate-mount-suppressed", {
        detail: {
          appId: options.appId,
          customerId: options.customerId,
          path: window.location.pathname
        }
      }));
      return existing.instance;
    }

    const preservedState = existing
      ? { initiallyOpen: existing.instance.isOpen, sessionId: existing.instance.sessionId }
      : null;
    if (existing) {
      registry.delete(key);
      existing.instance.destroy();
    }

    const instance = mountWidget({
      ...options,
      ...(preservedState ?? {})
    });
    const originalDestroy = instance.destroy;
    let destroyed = false;
    instance.destroy = () => {
      if (destroyed) {
        return;
      }
      destroyed = true;
      if (registry.get(key)?.instance === instance) {
        registry.delete(key);
      }
      originalDestroy();
    };
    registry.set(key, { fingerprint, instance });
    return instance;
  }

  function preconnectRuntime(runtimeUrl: string) {
    try {
      const origin = new URL(runtimeUrl, window.location.href).origin;
      if (preconnectedRuntimeOrigins.has(origin)) {
        return;
      }
      preconnectedRuntimeOrigins.add(origin);
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = origin;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    } catch {
      // Preconnect is opportunistic.
    }
  }

  function scriptOptions(script: HTMLScriptElement): WidgetOptions | null {
    const appId = attr(script, "data-app-id");
    const customerId = attr(script, "data-customer-id");
    const runtimeKey = attr(script, "data-runtime-key") || attr(script, "data-key");
    if (!appId && !customerId && !runtimeKey) {
      return null;
    }
    if (!appId || !customerId || !runtimeKey) {
      console.warn("Agent Platform widget requires data-app-id, data-customer-id, and data-runtime-key.");
      return null;
    }
    return {
      appId,
      customerId,
      runtimeKey,
      runtimeUrl: attr(script, "data-runtime-url") || defaultRuntimeUrl,
      sessionId: attr(script, "data-session-id") || undefined,
      title: attr(script, "data-title") || "Agent chat",
      launcherLabel: attr(script, "data-launcher-label") || "Chat",
      inputPlaceholder: attr(script, "data-input-placeholder") || "Ask a question",
      position: attr(script, "data-position") === "left" ? "left" : "right",
      theme: scriptTheme(attr(script, "data-theme")),
      primaryColor: attr(script, "data-primary-color") || undefined,
      stylePreset: widgetStylePreset(attr(script, "data-style-preset")),
      initiallyOpen: attr(script, "data-open") === "true",
      shopperContext: scriptShopperContext(script)
    };
  }

  function mountWidget(options: WidgetOptions): WidgetInstance {
    if (!options.appId || !options.customerId || !options.runtimeKey) {
      throw new Error("Agent Platform widget mount requires appId, customerId, and runtimeKey.");
    }
    if (options.stylePreset === "sotm" && !document.querySelector("style[data-sotm-widget-fonts]")) {
      const fonts = document.createElement("style");
      fonts.dataset.sotmWidgetFonts = "true";
      const fontBase = widgetFontBase || new URL("fonts/ui/", document.baseURI).href;
      fonts.textContent = sotmWidgetFonts.replaceAll("__SOTM_FONT_BASE__", fontBase);
      document.head.appendChild(fonts);
    }
    if (options.stylePreset !== "sotm" && widgetFontBase && !document.querySelector("style[data-agent-platform-fonts]") && !Array.from(document.fonts ?? []).some(face => face.family.includes("Nebula Sans"))) {
      const fonts = document.createElement("style"); fonts.dataset.agentPlatformFonts = "true";
      fonts.textContent = platformWidgetFonts.replaceAll("__UI_FONT_BASE__", widgetFontBase);
      document.head.appendChild(fonts);
    }

    const host = options.mount ?? document.createElement("div");
    if (!options.mount) {
      document.body.appendChild(host);
    }
    host.setAttribute(widgetAttribute, "true");
    host.setAttribute(widgetHostAttribute, "true");
    host.setAttribute("data-agent-platform-app-id", options.appId);
    host.setAttribute("data-agent-platform-customer-id", options.customerId);
    host.toggleAttribute("data-framer-app", window.location.hostname.endsWith(".framer.app"));
    preconnectRuntime(options.runtimeUrl ?? defaultRuntimeUrl);

    const shadow = host.attachShadow({ mode: "open" });
    const initialSessionId = ensureSessionId(options);
    const state = {
      sessionId: initialSessionId,
      open: options.initiallyOpen ?? false,
      busy: false,
      streaming: false,
      messages: loadTranscript(options, initialSessionId),
      error: "",
      lastFailedContent: "",
      config: fallbackConfig(options),
      voiceActive: false,
      voiceStatus: "off" as VoiceStatus,
      voiceError: "",
      lastVoiceTranscript: "",
      lastVoiceTranscriptAt: 0,
      voiceStarting: false,
      fashionSettingsOpen: false,
      fashionTryOn: null as FashionTryOnModal | null,
      pendingConfirmation: null as PendingConfirmation | null,
      nudgeVisible: false,
      nudgeTimer: 0,
      nudgeStarted: false,
      nudgeStopped: false,
      launcherInteracted: false,
      pendingChoice: ""
    };
    const stylePreset = options.stylePreset ?? "default";
    let geminiSocket: WebSocket | null = null;
    let mediaStream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let audioSource: MediaStreamAudioSourceNode | null = null;
    let audioProcessor: AudioWorkletNode | null = null;
    let audioSilenceGain: GainNode | null = null;
    let audioQueueTime = 0;
    let voicePlaybackTimer = 0;
    let geminiResumeHandle = "";
    let geminiReconnectAttempts = 0;
    let geminiReconnectTimer = 0;
    let geminiReconnectResetTimer = 0;
    let voiceGeneration = 0;
    let voiceRequested = false;
    let queuedVoiceTranscript: { text: string; provider: "gemini-live" | "openai-realtime" | "elevenlabs" | "vapi" } | null = null;
    const activePlaybackSources = new Set<AudioBufferSourceNode>();
    let streamRenderFrame = 0;
    let vapiClient: any = null;
    let vapiBound = false;
    let existingWidgetSessionCredential: ExistingWidgetSessionCredential | null = null;
    let existingWidgetSessionPromise: Promise<ExistingWidgetSessionCredential> | null = null;
    let fashionTryOnAbortController: AbortController | null = null;

    shadow.innerHTML = `
      <style>${styles(options.position ?? "right", options.theme ?? "light", options.primaryColor, stylePreset)}</style>
      <button class="apw-launcher" type="button" aria-label="Open agent chat">
        <span class="apw-launcher-label">${escapeHtml(state.config.launcher_label)}</span>
      </button>
      <section class="apw-panel" role="dialog" aria-modal="true" aria-label="${escapeHtml(stylePreset === "sotm" ? "SOTM chat" : state.config.title)}" tabindex="-1">
        <header>
          <strong>${renderWidgetTitle(state.config, stylePreset)}</strong>
          <span class="apw-header-actions">
            ${stylePreset === "fashion" ? `<button class="apw-refine" type="button" aria-label="Personalization and filters" title="Personalization and filters">Filters</button>` : ""}
            <button class="apw-new" type="button" aria-label="Start a new conversation" title="Start a new conversation">${stylePreset === "sotm" ? sotmResetIcon() : "New"}</button>
            <button class="apw-close" type="button" aria-label="Close agent chat">${stylePreset === "sotm" ? sotmCloseIcon() : "Close"}</button>
          </span>
        </header>
        <div class="apw-thread" role="log" aria-live="polite"></div>
        <form class="apw-composer">
          <textarea name="message" rows="1" enterkeyhint="send" aria-label="${escapeAttribute(state.config.input_placeholder)}" placeholder="${escapeAttribute(state.config.input_placeholder)}"></textarea>
          <button class="apw-voice" type="button" aria-label="Start voice">Voice</button>
          <button type="submit">Send</button>
        </form>
        <div class="apw-voice-status" aria-live="polite"></div>
        <div class="apw-fashion-settings" aria-live="polite"></div>
        <div class="apw-try-on-modal-host" aria-live="polite"></div>
        <div class="apw-confirmation-host" aria-live="polite"></div>
      </section>
    `;

    const launcher = required<HTMLButtonElement>(shadow, ".apw-launcher");
    const panel = required<HTMLElement>(shadow, ".apw-panel");
    const title = required<HTMLElement>(shadow, "header strong");
    const styleElement = required<HTMLStyleElement>(shadow, "style");
    const closeButton = required<HTMLButtonElement>(shadow, ".apw-close");
    const newButton = required<HTMLButtonElement>(shadow, ".apw-new");
    const refineButton = shadow.querySelector<HTMLButtonElement>(".apw-refine");
    const thread = required<HTMLElement>(shadow, ".apw-thread");
    const form = required<HTMLFormElement>(shadow, ".apw-composer");
    const textarea = required<HTMLTextAreaElement>(shadow, "textarea");
    const voiceButton = required<HTMLButtonElement>(shadow, ".apw-voice");
    const sendButton = required<HTMLButtonElement>(shadow, ".apw-composer button[type='submit']");
    const voiceStatus = required<HTMLElement>(shadow, ".apw-voice-status");
    const fashionSettings = required<HTMLElement>(shadow, ".apw-fashion-settings");
    const tryOnModalHost = required<HTMLElement>(shadow, ".apw-try-on-modal-host");
    const confirmationHost = required<HTMLElement>(shadow, ".apw-confirmation-host");
    const phoneViewport = window.matchMedia("(max-width: 767px)");
    const inertBackground = new Map<HTMLElement, boolean>();
    let pendingThreadScroll: "active" | "bottom" | "preserve" | null =
      state.open && state.messages.length ? "bottom" : null;
    let savedThreadScrollTop = 0;
    let pageScrollLock: {
      htmlOverflow: string;
      bodyOverflow: string;
      bodyOverscrollBehavior: string;
    } | null = null;

    function render() {
      const position = state.config.placement === "bottom-left" ? "left" : "right";
      styleElement.textContent = styles(
        position,
        options.theme ?? "light",
        configPrimaryColor(state.config, options.primaryColor),
        stylePreset
      );
      panel.setAttribute("data-style-preset", stylePreset);
      panel.toggleAttribute("data-open", state.open);
      panel.toggleAttribute("data-fashion-settings-open", stylePreset === "fashion" && state.fashionSettingsOpen);
      panel.toggleAttribute("data-try-on-open", stylePreset === "fashion" && Boolean(state.fashionTryOn));
      launcher.toggleAttribute("data-hidden", state.open);
      launcher.toggleAttribute("data-nudge", state.nudgeVisible && !state.open);
      launcher.dataset.nudgeStyle = state.config.nudge?.style || "none";
      if (stylePreset === "sotm") {
        const colors = state.config.colors;
        const launcherColors = {
          "--apw-launcher-bg": configPrimaryColor(state.config, options.primaryColor),
          "--apw-launcher-fg": colorValue(colors?.buttonForeground),
          "--apw-nudge-start": colorValue(colors?.startColor),
          "--apw-nudge-accent": colorValue(colors?.accentColor),
          "--apw-nudge-end": colorValue(colors?.endColor)
        };
        for (const [property, value] of Object.entries(launcherColors)) {
          if (value) launcher.style.setProperty(property, value);
          else launcher.style.removeProperty(property);
        }
        launcher.style.setProperty("--apw-nudge-duration", `${Math.max(300, Number(state.config.nudge?.animationMs ?? 1800))}ms`);
        sendButton.setAttribute("aria-label", state.busy ? "Sending message" : "Send message");
        sendButton.title = "Send message";
        closeButton.title = "Close agent chat";
      }
      launcher.innerHTML = stylePreset === "sotm"
        ? `<span class="apw-launcher-label">${sotmSignalIcon("apw-sotm-launcher-signal")}<span>${escapeHtml(state.config.launcher_label || "Ask SOTM")}</span></span>`
        : `<span class="apw-launcher-label">${escapeHtml(state.config.launcher_label || "Chat")}</span>`;
      title.innerHTML = renderWidgetTitle(state.config, stylePreset);
      textarea.placeholder =
        stylePreset === "fashion" && state.voiceActive
          ? "Listening..."
          : stylePreset === "sonic-insurance"
            ? "Ask Scout…"
            : stylePreset === "sotm"
              ? "Ask SOTM anything…"
              : state.config.input_placeholder || "Ask a question";
      textarea.setAttribute("aria-label", state.config.input_placeholder || "Ask a question");
      const voiceConfig = normalizedVoiceConfig(state.config.voice);
      const voiceAvailable = isVoiceAvailable(voiceConfig);
      form.toggleAttribute("data-voice", voiceAvailable);
      form.toggleAttribute("data-voice-active", state.voiceActive);
      voiceButton.hidden = !voiceAvailable;
      voiceButton.disabled = Boolean(
        !voiceAvailable ||
        (state.busy && !state.voiceActive) ||
        (state.voiceStarting && !state.voiceActive)
      );
      voiceButton.textContent = state.voiceActive ? "Stop" : "Voice";
      voiceButton.setAttribute("aria-label", state.voiceActive ? "Stop voice" : "Start voice");
      voiceButton.setAttribute("aria-pressed", state.voiceActive ? "true" : "false");
      voiceButton.setAttribute("aria-busy", state.voiceStarting ? "true" : "false");
      voiceButton.title = voiceAvailable
        ? state.voiceError || voiceStatusLabel(state.voiceStatus, voiceConfig.provider || "") || "Start voice"
        : voiceUnavailableLabel(voiceConfig);
      voiceButton.toggleAttribute("data-active", state.voiceActive);
      voiceButton.setAttribute("data-status", state.voiceStatus);
      voiceStatus.textContent = voiceAvailable
        ? state.voiceError || (stylePreset === "sonic-insurance" && state.voiceStatus === "off"
          ? ""
          : voiceStatusLabel(state.voiceStatus, voiceConfig.provider || ""))
        : "";
      voiceStatus.toggleAttribute("data-error", Boolean(state.voiceError));
      fashionSettings.innerHTML =
        stylePreset === "fashion" && state.fashionSettingsOpen ? renderFashionSettings(state.config) : "";
      tryOnModalHost.innerHTML =
        stylePreset === "fashion" && state.fashionTryOn ? renderFashionTryOnModal(state.fashionTryOn) : "";
      confirmationHost.innerHTML = state.pendingConfirmation ? renderPendingConfirmation(state.pendingConfirmation) : "";
      sendButton.innerHTML =
        stylePreset === "sotm"
          ? state.busy
            ? `<span class="apw-sotm-send-busy" aria-hidden="true">•••</span>`
            : sotmSendIcon()
          : state.busy
            ? "..."
            : "Send";
      textarea.disabled = state.busy;
      form.setAttribute("aria-busy", state.busy ? "true" : "false");
      thread.setAttribute("aria-busy", state.busy ? "true" : "false");
      syncComposerState();
      const latestInsuranceRichIndex =
        stylePreset === "sonic-insurance"
          ? state.messages.reduce(
              (latest, message, index) =>
                message.role === "assistant" &&
                normalizeActions(message.actions).some((action) => ["intake_form", "coverage_card"].includes(action.type))
                  ? index
                  : latest,
              -1
            )
          : -1;
      const rows = state.messages
        .map((message, index) =>
          renderWidgetMessage(message, stylePreset, {
            historicalInsuranceRich:
              stylePreset === "sonic-insurance" &&
              index < latestInsuranceRichIndex &&
              message.role === "assistant" &&
              normalizeActions(message.actions).some((action) => action.type === "intake_form"),
            nextUserContent: state.messages[index + 1]?.role === "user" ? state.messages[index + 1]?.content : ""
          })
        )
        .join("");
      const pending = state.busy && !state.streaming ? renderTypingIndicator(state.voiceActive, stylePreset) : "";
      const error = state.error
        ? `<div class="apw-error">${escapeHtml(state.error)}${
            state.lastFailedContent ? ` <button class="apw-retry" type="button">Retry</button>` : ""
          }</div>`
        : "";
      const empty = state.config.guest_greeting || "No messages yet";
      const emptyPresentation =
        stylePreset === "sonic-insurance"
          ? renderInsuranceWelcome()
          : stylePreset === "sotm"
            ? renderSotmWelcome(state.config)
            : `<div class="apw-empty">${escapeHtml(empty)}</div>`;
      thread.innerHTML = `${rows || emptyPresentation}${pending}${error}`;
      for (const button of thread.querySelectorAll<HTMLButtonElement>(".apw-quick-reply")) {
        button.disabled = state.busy;
        button.setAttribute("aria-disabled", state.busy ? "true" : "false");
        const selected = Boolean(state.pendingChoice) && button.dataset.displayMessage === state.pendingChoice;
        button.toggleAttribute("data-selected", selected);
        if (selected) {
          button.setAttribute("aria-pressed", "true");
        }
      }
      const commerceMessages =
        stylePreset === "fashion" ? Array.from(thread.querySelectorAll<HTMLElement>(".apw-message[data-commerce='true']")) : [];
      const latestCommerce = commerceMessages[commerceMessages.length - 1];
      if (state.open) {
        if (latestCommerce) {
          thread.scrollTop = Math.max(0, latestCommerce.offsetTop - 96);
        } else if (pendingThreadScroll === "active") {
          const activeCards = thread.querySelectorAll<HTMLElement>(
            ".apw-message[data-insurance-rich='true'] .apw-insurance-intake, .apw-message[data-insurance-rich='true'] .apw-insurance-estimate"
          );
          const activeCard = activeCards[activeCards.length - 1];
          if (activeCard) {
            const threadRect = thread.getBoundingClientRect();
            const cardRect = activeCard.getBoundingClientRect();
            thread.scrollTop = Math.max(0, thread.scrollTop + cardRect.top - threadRect.top - 12);
          } else {
            thread.scrollTop = thread.scrollHeight;
          }
        } else if (pendingThreadScroll === "bottom") {
          thread.scrollTop = thread.scrollHeight;
        } else {
          thread.scrollTop = Math.min(savedThreadScrollTop, Math.max(0, thread.scrollHeight - thread.clientHeight));
        }
        savedThreadScrollTop = thread.scrollTop;
      }
      pendingThreadScroll = null;
      syncPageScrollLock();
    }

    function syncComposerState() {
      const hasContent = Boolean(textarea.value.trim());
      form.toggleAttribute("data-has-content", hasContent);
      sendButton.disabled = state.busy || !hasContent;
      if (stylePreset === "sonic-insurance") {
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(112, Math.max(48, textarea.scrollHeight))}px`;
      }
    }

    function syncViewportMetrics() {
      const viewport = window.visualViewport;
      host.style.setProperty("--apw-visual-viewport-height", `${Math.round(viewport?.height ?? window.innerHeight)}px`);
      host.style.setProperty("--apw-visual-viewport-width", `${Math.round(viewport?.width ?? window.innerWidth)}px`);
      host.style.setProperty("--apw-visual-viewport-top", `${Math.round(viewport?.offsetTop ?? 0)}px`);
      host.style.setProperty("--apw-visual-viewport-left", `${Math.round(viewport?.offsetLeft ?? 0)}px`);
      syncPageScrollLock();
    }

    function syncPageScrollLock(forceUnlock = false) {
      const shouldLock = !forceUnlock && state.open && phoneViewport.matches;
      panel.setAttribute("aria-modal", String(shouldLock));
      if (shouldLock && !pageScrollLock) {
        pageScrollLock = {
          htmlOverflow: document.documentElement.style.overflow,
          bodyOverflow: document.body.style.overflow,
          bodyOverscrollBehavior: document.body.style.overscrollBehavior
        };
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.body.style.overscrollBehavior = "none";
        let branch: HTMLElement | null = host;
        while (branch?.parentElement) {
          for (const sibling of Array.from(branch.parentElement.children)) {
            if (sibling instanceof HTMLElement && sibling !== branch && !["SCRIPT","STYLE","LINK"].includes(sibling.tagName)) {
              inertBackground.set(sibling, sibling.inert); sibling.inert = true;
            }
          }
          if (branch.parentElement === document.body) break;
          branch = branch.parentElement;
        }
      } else if (!shouldLock && pageScrollLock) {
        document.documentElement.style.overflow = pageScrollLock.htmlOverflow;
        document.body.style.overflow = pageScrollLock.bodyOverflow;
        document.body.style.overscrollBehavior = pageScrollLock.bodyOverscrollBehavior;
        inertBackground.forEach((inert, element) => { element.inert = inert; });
        inertBackground.clear();
        pageScrollLock = null;
      }
    }

    function focusConversationEntry() {
      if (stylePreset === "sonic-insurance" && phoneViewport.matches) {
        panel.focus({ preventScroll: true });
      } else {
        textarea.focus({ preventScroll: true });
      }
    }

    function activeInsuranceIntakeAction(): WidgetAction | null {
      if (stylePreset !== "sonic-insurance") {
        return null;
      }
      for (let index = state.messages.length - 1; index >= 0; index -= 1) {
        const message = state.messages[index];
        if (message?.role !== "assistant") {
          continue;
        }
        const action = normalizeActions(message.actions).find((candidate) => candidate.type === "intake_form");
        if (!action) {
          continue;
        }
        const alreadyAnswered = state.messages.slice(index + 1).some((candidate) => {
          if (candidate.role !== "user") {
            return false;
          }
          return resolveInsuranceIntakeInput(action, candidate.content).kind === "resolved";
        });
        return alreadyAnswered ? null : action;
      }
      return null;
    }

    function resolveActiveInsuranceTurn(content: string): InsuranceIntakeResolution | null {
      const action = activeInsuranceIntakeAction();
      return action ? resolveInsuranceIntakeInput(action, content) : null;
    }

    function appendInsuranceClarification(userContent: string, message: string) {
      state.messages.push({ role: "user", content: userContent });
      state.messages.push({
        role: "assistant",
        content: message,
        speech: { content: message, text: message }
      });
      state.error = "";
      state.lastFailedContent = "";
      saveTranscript(options, state.sessionId, state.messages);
      pendingThreadScroll = "bottom";
    }

    async function sendMessage(content: string, reuseLastUser = false, displayContent = content) {
      const intakeResolution = resolveActiveInsuranceTurn(content);
      if (intakeResolution?.kind === "ambiguous") {
        if (!reuseLastUser) {
          appendInsuranceClarification(displayContent, intakeResolution.message);
        }
        render();
        return;
      }
      if (intakeResolution?.kind === "resolved") {
        content = intakeResolution.prompt;
        displayContent = intakeResolution.displayContent;
      }
      if (!reuseLastUser) {
        state.messages.push({ role: "user", content: displayContent });
      }
      state.pendingChoice = displayContent;
      state.error = "";
      state.lastFailedContent = "";
      state.busy = true;
      state.streaming = false;
      saveTranscript(options, state.sessionId, state.messages);
      pendingThreadScroll = "bottom";
      render();
      let streamedMessage: WidgetMessage | null = null;
      try {
        let payload: RuntimeChatResponse;
        try {
          payload = await requestChatStream(content, (delta) => {
            if (!delta) {
              return;
            }
            if (!streamedMessage) {
              streamedMessage = { role: "assistant", content: "" };
              state.messages.push(streamedMessage);
              state.streaming = true;
            }
            streamedMessage.content += delta;
            scheduleStreamRender();
          }, (evidence) => {
            if (stylePreset !== "sotm") return;
            const actions = normalizeActions(evidence.actions);
            if (!actions.length) return;
            if (!streamedMessage) {
              streamedMessage = { role: "assistant", content: "" };
              state.messages.push(streamedMessage);
              state.streaming = true;
            }
            streamedMessage.actions = actions;
            streamedMessage.sources = normalizeSources(evidence.sources);
            scheduleStreamRender();
          });
        } catch (streamError) {
          if (streamedMessage) {
            throw streamError;
          }
          payload = await requestChatJson(content);
        }
        const assistantMessage = chatMessageFromPayload(payload);
        if (streamedMessage) {
          Object.assign(streamedMessage, assistantMessage);
        } else {
          state.messages.push(assistantMessage);
        }
        saveTranscript(options, state.sessionId, state.messages);
      } catch (error) {
        const partialMessage = streamedMessage as WidgetMessage | null;
        if (partialMessage && !partialMessage.content.trim()) {
          state.messages = state.messages.filter((message) => message !== partialMessage);
        }
        state.error = error instanceof Error ? error.message : "Chat request failed.";
        state.lastFailedContent = content;
      } finally {
        state.busy = false;
        state.streaming = false;
        state.pendingChoice = "";
        const latestMessage = state.messages[state.messages.length - 1];
        const hasActiveRichAction =
          latestMessage?.role === "assistant" &&
          normalizeActions(latestMessage.actions).some((action) => ["intake_form", "coverage_card"].includes(action.type));
        pendingThreadScroll = hasActiveRichAction ? "active" : "bottom";
        render();
      }
    }

    async function requestChatJson(content: string): Promise<RuntimeChatResponse> {
      const response = await fetch(`${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/chat`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${options.runtimeKey}`,
          "Content-Type": "application/json",
          "X-Agent-Surface": "embedded_widget"
        },
        body: JSON.stringify(chatRequestPayload(content))
      });
      if (!response.ok) {
        throw new Error(await responseMessage(response));
      }
      return (await response.json()) as RuntimeChatResponse;
    }

    async function requestChatStream(content: string, onDelta: (delta: string) => void,
      onEvidence: (payload: Record<string, unknown>) => void): Promise<RuntimeChatResponse> {
      const response = await fetch(`${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/chat/stream`, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${options.runtimeKey}`,
          "Content-Type": "application/json",
          "X-Agent-Surface": "embedded_widget"
        },
        body: JSON.stringify({ ...chatRequestPayload(content), stream: true })
      });
      if (!response.ok) {
        throw new Error(await responseMessage(response));
      }
      if (!response.body) {
        throw new Error("Runtime stream is unavailable.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalPayload: RuntimeChatResponse | null = null;
      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done });
        const parsed = consumeSseEvents(buffer);
        buffer = parsed.remainder;
        for (const event of parsed.events) {
          if (event.data === "[DONE]") {
            continue;
          }
          const payload = parseRecord(event.data);
          if (event.event === "token") {
            onDelta(stringValue(payload.delta));
          } else if (event.event === "turn.evidence") {
            onEvidence(payload);
          } else if (event.event === "done") {
            finalPayload = payload as RuntimeChatResponse;
          }
        }
        if (done) {
          break;
        }
      }
      if (buffer.trim()) {
        const event = parseSseEvent(buffer);
        if (event?.event === "done") {
          finalPayload = parseRecord(event.data) as RuntimeChatResponse;
        }
      }
      if (!finalPayload?.message) {
        throw new Error("Runtime stream ended without a final response.");
      }
      return finalPayload;
    }

    function chatRequestPayload(content: string): Record<string, unknown> {
      const messages = state.messages
        .filter((message) => message.role === "user" || message.role === "assistant")
        .slice(-8)
        .map((message) => ({ role: message.role, content: message.content }));
      const lastUserIndex = messages.map((message) => message.role).lastIndexOf("user");
      if (lastUserIndex >= 0) {
        messages[lastUserIndex] = { role: "user", content };
      } else {
        messages.push({ role: "user", content });
      }
      return {
        customer_id: options.customerId,
        app_id: options.appId,
        session_id: state.sessionId,
        messages,
        page_context: {
          url: window.location.href,
          path: window.location.pathname,
          title: document.title
        },
        shopper_context: shopperContextPayload(options)
      };
    }

    function chatMessageFromPayload(payload: RuntimeChatResponse): WidgetMessage {
      const rawContent = stringValue(payload.message?.content);
      const safeContent = publicVisitorText(rawContent);
      if (rawContent !== safeContent) {
        window.dispatchEvent(
          new CustomEvent("agent-platform-widget:unsafe-response-blocked", {
            detail: {
              appId: options.appId,
              customerId: options.customerId,
              sessionId: state.sessionId,
              reason: "internal_identifier",
            },
          })
        );
      }
      return {
        ...payload.message,
        content: safeContent,
        protocolVersion: payload.protocol_version ?? payload.protocolVersion ?? "2",
        actions: normalizeActions(payload.actions ?? payload.tool_context?.actions),
        sources: normalizeSources(payload.sources).length ? normalizeSources(payload.sources) : sourcesFromToolContext(payload.tool_context),
        visualSummary: payload.visual_summary ?? null,
        speech: publicVisitorSpeech(payload.speech),
        trust: payload.trust ?? parseRecord(payload.tool_context?.trust),
        requiredConfirmation: payload.required_confirmation ?? parseRecord(payload.tool_context?.required_confirmation),
        fallbackText: publicVisitorText(
          stringValue(payload.fallback_text ?? payload.fallbackText ?? payload.message?.content)
        ) || null,
        commerceContext:
          payload.commerce_context ??
          payload.commerceContext ??
          nonEmptyRecord(payload.tool_context?.commerce_context) ??
          nonEmptyRecord(parseRecord(payload.tool_context?.protocol)?.commerceContext) ??
          null
      };
    }

    function scheduleStreamRender() {
      if (streamRenderFrame) {
        return;
      }
      streamRenderFrame = window.requestAnimationFrame(() => {
        streamRenderFrame = 0;
        if (thread.scrollHeight - savedThreadScrollTop - thread.clientHeight < 80) {
          pendingThreadScroll = "bottom";
        }
        render();
      });
    }

    function consumeSseEvents(buffer: string): { events: Array<{ event: string; data: string }>; remainder: string } {
      const events: Array<{ event: string; data: string }> = [];
      let remainder = buffer;
      while (true) {
        const boundary = remainder.search(/\r?\n\r?\n/);
        if (boundary < 0) {
          return { events, remainder };
        }
        const block = remainder.slice(0, boundary);
        const delimiter = remainder.slice(boundary).match(/^\r?\n\r?\n/)?.[0] || "\n\n";
        remainder = remainder.slice(boundary + delimiter.length);
        const event = parseSseEvent(block);
        if (event) {
          events.push(event);
        }
      }
    }

    function parseSseEvent(block: string): { event: string; data: string } | null {
      const lines = block.split(/\r?\n/);
      let event = "message";
      const data: string[] = [];
      for (const line of lines) {
        if (line.startsWith("event:")) {
          event = line.slice(6).trim() || "message";
        } else if (line.startsWith("data:")) {
          data.push(line.slice(5).trimStart());
        }
      }
      if (!data.length) {
        return null;
      }
      return { event, data: data.join("\n") };
    }

    launcher.addEventListener("click", () => {
      state.open = true;
      state.nudgeVisible = false;
      state.launcherInteracted = true;
      state.nudgeStopped = Boolean(state.config.nudge?.stopAfterOpen);
      window.clearTimeout(state.nudgeTimer);
      render();
      focusConversationEntry();
    });
    closeButton.addEventListener("click", () => {
      savedThreadScrollTop = thread.scrollTop;
      if (state.voiceActive || state.voiceStarting) {
        stopVoice();
      }
      closeFashionTryOn();
      state.open = false;
      render();
      launcher.focus({ preventScroll: true });
      scheduleNudge();
    });
    newButton.addEventListener("click", () => {
      const resetMessage = stylePreset === "sotm"
        ? "Start a new conversation? Your current conversation will be cleared."
        : "Start a new conversation? Your current quote progress will be cleared.";
      if (state.messages.length && !window.confirm(resetMessage)) {
        return;
      }
      closeFashionTryOn();
      existingWidgetSessionCredential = null;
      existingWidgetSessionPromise = null;
      state.sessionId = nextSessionId();
      state.messages = [];
      state.error = "";
      state.lastFailedContent = "";
      state.pendingChoice = "";
      state.fashionSettingsOpen = false;
      state.fashionTryOn = null;
      stopVoice();
      try {
        window.localStorage.setItem(sessionStorageKey(options), state.sessionId);
      } catch {
        // Session persistence is best-effort.
      }
      savedThreadScrollTop = 0;
      pendingThreadScroll = "bottom";
      render();
      focusConversationEntry();
    });
    refineButton?.addEventListener("click", () => {
      if (stylePreset === "fashion") {
        state.fashionSettingsOpen = !state.fashionSettingsOpen;
        render();
        return;
      }
      textarea.focus();
    });
    thread.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const actionLink = target.closest<HTMLAnchorElement>(".apw-action-open");
      if (actionLink) {
        if (actionLink.hasAttribute("data-confirm")) {
          event.preventDefault();
          state.pendingConfirmation = pendingConfirmationFromElement(actionLink, options, state.sessionId);
          dispatchActionEvent(actionLink, "prompted", options, state.sessionId);
          render();
          return;
        }
        dispatchActionEvent(actionLink, "opened", options, state.sessionId);
        return;
      }
      const quickReply = target.closest<HTMLButtonElement>(".apw-quick-reply[data-prompt]");
      if (quickReply) {
        const prompt = quickReply.getAttribute("data-prompt") || "";
        const displayContent = quickReply.getAttribute("data-display-message") || prompt;
        if (quickReply.hasAttribute("data-confirm")) {
          state.pendingConfirmation = pendingConfirmationFromElement(quickReply, options, state.sessionId);
          dispatchActionEvent(quickReply, "prompted", options, state.sessionId);
          render();
          return;
        }
        if (prompt && !state.busy) {
          if (quickReply.getAttribute("data-action-type") === "switch_to_text") {
            stopVoice();
          }
          dispatchActionEvent(quickReply, "prompted", options, state.sessionId);
          void sendMessage(prompt, false, displayContent);
        }
        return;
      }
      const tryOn = target.closest<HTMLButtonElement>(".apw-try-on[data-prompt]");
      if (tryOn) {
        if (stylePreset === "fashion") {
          state.fashionTryOn = tryOnModalFromElement(tryOn);
          dispatchActionEvent(tryOn, "prompted", options, state.sessionId);
          render();
          return;
        }
        const prompt = tryOn.getAttribute("data-prompt") || "";
        if (prompt && !state.busy) {
          dispatchActionEvent(tryOn, "prompted", options, state.sessionId);
          void sendMessage(prompt);
        }
        return;
      }
      if (target.classList.contains("apw-retry") && state.lastFailedContent) {
        void sendMessage(state.lastFailedContent, true);
      }
    });
    thread.addEventListener("input", (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.classList.contains("apw-price-input")) {
        updatePriceRange(target);
      }
    });
    thread.addEventListener("scroll", () => {
      savedThreadScrollTop = thread.scrollTop;
    }, { passive: true });
    thread.addEventListener("wheel", (event) => {
      if (!state.open || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
      }
      const maxScrollTop = Math.max(0, thread.scrollHeight - thread.clientHeight);
      if (maxScrollTop <= 0) {
        return;
      }
      const deltaScale = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? thread.clientHeight
          : 1;
      const nextScrollTop = Math.max(0, Math.min(maxScrollTop, thread.scrollTop + event.deltaY * deltaScale));
      event.preventDefault();
      event.stopPropagation();
      if (nextScrollTop !== thread.scrollTop) {
        thread.scrollTop = nextScrollTop;
        savedThreadScrollTop = nextScrollTop;
      }
    }, { passive: false });
    thread.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains("apw-insurance-location")) return;
      event.preventDefault();
      const input = form.querySelector<HTMLInputElement>("input");
      const value = input?.value.trim() || "";
      if (value && !state.busy) {
        const displayContent = insuranceLocationDisplay(value);
        void sendMessage(`The property is located in ${displayContent}.`, false, displayContent);
      }
    });
    shadow.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || thread.contains(target)) {
        return;
      }
      if (target.closest("[data-fashion-settings-close]")) {
        state.fashionSettingsOpen = false;
        render();
        return;
      }
      if (target.closest("[data-try-on-close]")) {
        closeFashionTryOn();
        render();
        return;
      }
      if (target.closest("[data-confirmation-cancel]")) {
        if (state.pendingConfirmation) {
          dispatchActionDetail(pendingActionEvent(state.pendingConfirmation, "cancelled"), options);
        }
        state.pendingConfirmation = null;
        render();
        return;
      }
      if (target.closest("[data-confirmation-confirm]")) {
        const pending = state.pendingConfirmation;
        if (pending) {
          dispatchActionDetail(pendingActionEvent(pending, "opened"), options);
          const targetUrl = pending.url;
          const prompt = pending.prompt || "";
          const displayContent = pending.displayContent || pending.label || prompt;
          state.pendingConfirmation = null;
          render();
          if (["preference_update", "preference_forget", "preference_opt_out"].includes(pending.actionType)) {
            void executePersonalizationMutation(pending);
          } else if (safeUrl(targetUrl)) {
            window.open(targetUrl, "_blank", "noopener,noreferrer");
          } else if (prompt && !state.busy) {
            if (pending.actionType === "switch_to_text") {
              stopVoice();
            }
            void sendMessage(prompt, false, displayContent);
          }
        }
        return;
      }
      const quickReply = target.closest<HTMLButtonElement>(".apw-quick-reply[data-prompt]");
      if (quickReply) {
        const prompt = quickReply.getAttribute("data-prompt") || "";
        if (quickReply.hasAttribute("data-confirm")) {
          state.pendingConfirmation = pendingConfirmationFromElement(quickReply, options, state.sessionId);
          dispatchActionEvent(quickReply, "prompted", options, state.sessionId);
          render();
          return;
        }
        if (prompt && !state.busy) {
          state.fashionSettingsOpen = false;
          if (quickReply.getAttribute("data-action-type") === "switch_to_text") {
            stopVoice();
          }
          dispatchActionEvent(quickReply, "prompted", options, state.sessionId);
          void sendMessage(prompt);
        }
      }
    });
    shadow.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) {
        return;
      }
      if (target.classList.contains("apw-try-on-consent")) {
        if (!state.fashionTryOn) {
          return;
        }
        if (!target.checked) {
          fashionTryOnAbortController?.abort();
          fashionTryOnAbortController = null;
          revokeTryOnPreview();
        }
        state.fashionTryOn = {
          ...state.fashionTryOn,
          consentConfirmed: target.checked,
          previewUrl: target.checked ? state.fashionTryOn.previewUrl : "",
          fileName: target.checked ? state.fashionTryOn.fileName : "",
          generatedImages: target.checked ? state.fashionTryOn.generatedImages : [],
          status: target.checked ? "Consent confirmed. Choose a photo to continue." : "",
          error: "",
          isGenerating: false
        };
        render();
        shadow.querySelector<HTMLInputElement>(".apw-try-on-upload")?.focus();
        return;
      }
      if (!target.classList.contains("apw-try-on-upload")) {
        return;
      }
      const file = target.files?.[0];
      if (!file || !state.fashionTryOn) {
        return;
      }
      void submitFashionTryOnUpload(file);
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const content = textarea.value.trim();
      if (!content || state.busy) {
        return;
      }
      textarea.value = "";
      syncComposerState();
      void sendMessage(content);
    });
    textarea.addEventListener("input", syncComposerState);
    textarea.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" || event.shiftKey || event.isComposing) {
        return;
      }
      event.preventDefault();
      form.requestSubmit();
    });
    shadow.addEventListener("keydown", (event) => {
      if (event instanceof KeyboardEvent && event.key === "Tab" && state.open && phoneViewport.matches) {
        const activeDialog = panel.querySelector<HTMLElement>('.apw-try-on-dialog[aria-modal="true"]') ?? panel;
        const controls = Array.from(activeDialog.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]')).filter(element => element.getClientRects().length > 0 && !element.closest("[hidden]"));
        const first = controls[0], last = controls.at(-1), active = shadow.activeElement;
        if (first && last && event.shiftKey && (active === first || active === panel)) { event.preventDefault(); last.focus(); }
        else if (first && last && !event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
      }
      if (event instanceof KeyboardEvent && event.key === "Escape" && state.open) {
        savedThreadScrollTop = thread.scrollTop;
        if (state.voiceActive || state.voiceStarting) {
          stopVoice();
        }
        closeFashionTryOn();
        state.open = false;
        render();
        launcher.focus({ preventScroll: true });
        scheduleNudge();
      }
    });
    voiceButton.addEventListener("click", () => {
      if (state.voiceActive) {
        stopVoice();
        render();
        return;
      }
      if (state.voiceStarting) {
        return;
      }
      void startVoice();
    });

    function handlePageHide() {
      if (state.voiceActive || state.voiceStarting) {
        stopVoice();
      }
      closeFashionTryOn();
    }

    function tryOnModalFromElement(element: HTMLElement): FashionTryOnModal {
      return {
        title: element.getAttribute("data-title") || "Selected item",
        imageUrl: element.getAttribute("data-image-url") || "",
        productUrl: element.getAttribute("data-product-url") || "",
        prompt: element.getAttribute("data-prompt") || "",
        previewUrl: "",
        fileName: "",
        generatedImages: [],
        status: "",
        error: "",
        isGenerating: false,
        consentConfirmed: false
      };
    }

    function revokeTryOnPreview() {
      if (state.fashionTryOn?.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(state.fashionTryOn.previewUrl);
      }
    }

    function closeFashionTryOn() {
      fashionTryOnAbortController?.abort();
      fashionTryOnAbortController = null;
      revokeTryOnPreview();
      state.fashionTryOn = null;
    }

    async function submitFashionTryOnUpload(file: File) {
      if (!state.fashionTryOn) {
        return;
      }
      if (!state.fashionTryOn.consentConfirmed) {
        state.fashionTryOn = {
          ...state.fashionTryOn,
          status: "Confirm photo use before choosing a file.",
          error: "Confirm photo use before choosing a file.",
          isGenerating: false
        };
        render();
        return;
      }
      if (!/^image\/(png|jpeg|jpg|webp)$/.test(file.type || "")) {
        state.fashionTryOn = {
          ...state.fashionTryOn,
          status: "Upload a PNG, JPEG, or WebP image.",
          error: "Upload a PNG, JPEG, or WebP image.",
          isGenerating: false
        };
        render();
        return;
      }
      if (file.size > 8_000_000) {
        state.fashionTryOn = {
          ...state.fashionTryOn,
          status: "Upload an image under 8 MB.",
          error: "Upload an image under 8 MB.",
          isGenerating: false
        };
        render();
        return;
      }
      revokeTryOnPreview();
      fashionTryOnAbortController?.abort();
      const requestController = new AbortController();
      fashionTryOnAbortController = requestController;
      const previewUrl = URL.createObjectURL(file);
      state.fashionTryOn = {
        ...state.fashionTryOn,
        previewUrl,
        fileName: file.name,
        generatedImages: [],
        status: "Generating try-on preview...",
        error: "",
        isGenerating: true
      };
      render();
      try {
        const personImageDataUrl = await readFileAsDataUrl(file);
        if (
          requestController.signal.aborted ||
          !state.fashionTryOn ||
          !state.fashionTryOn.consentConfirmed ||
          state.fashionTryOn.previewUrl !== previewUrl
        ) {
          return;
        }
        const payload = (await existingWidgetJsonRequest(
          "/try-on",
          {
            session_id: state.sessionId,
            product_title: state.fashionTryOn.title,
            product_url: state.fashionTryOn.productUrl || null,
            product_image_url: state.fashionTryOn.imageUrl,
            person_image_data_url: personImageDataUrl,
            consent_confirmed: true,
            sample_count: 1
          },
          requestController.signal
        )) as {
          generated_images?: unknown;
          message?: string;
          detail?: string;
        };
        const generatedImages = stringArray(payload.generated_images);
        if (!generatedImages.length) {
          throw new Error("Try-on returned no preview image.");
        }
        if (!state.fashionTryOn || state.fashionTryOn.previewUrl !== previewUrl) {
          return;
        }
        state.fashionTryOn = {
          ...state.fashionTryOn,
          generatedImages,
          status: payload.message || "Try-on preview ready.",
          error: "",
          isGenerating: false
        };
      } catch (error) {
        if (requestController.signal.aborted) {
          return;
        }
        if (!state.fashionTryOn || state.fashionTryOn.previewUrl !== previewUrl) {
          return;
        }
        const message = error instanceof Error ? error.message : "Try-on is unavailable right now.";
        state.fashionTryOn = {
          ...state.fashionTryOn,
          generatedImages: [],
          status: message,
          error: message,
          isGenerating: false
        };
      } finally {
        if (fashionTryOnAbortController === requestController) {
          fashionTryOnAbortController = null;
        }
        render();
      }
    }

    async function existingWidgetSessionToken(forceRefresh = false): Promise<string> {
      const now = Math.floor(Date.now() / 1000);
      if (
        !forceRefresh &&
        existingWidgetSessionCredential?.sessionId === state.sessionId &&
        existingWidgetSessionCredential.expiresAt > now + 30
      ) {
        return existingWidgetSessionCredential.token;
      }
      if (!forceRefresh && existingWidgetSessionPromise) {
        return (await existingWidgetSessionPromise).token;
      }
      const requestedSessionId = state.sessionId;
      const bootstrapPromise = (async (): Promise<ExistingWidgetSessionCredential> => {
        const response = await fetch(
          `${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/shopify-widget/${encodeURIComponent(
            options.customerId
          )}/${encodeURIComponent(options.appId)}/session/bootstrap`,
          {
            method: "POST",
            credentials: "omit",
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${options.runtimeKey}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              session_id: requestedSessionId,
              store_domain: window.location.hostname || "",
              store_id: ""
            })
          }
        );
        const payload = parseRecord(await response.json().catch(() => ({})));
        if (!response.ok) {
          throw new Error(
            stringValue(payload.detail) || stringValue(payload.message) || "Secure widget session could not be started."
          );
        }
        const token = stringValue(payload.token);
        const expiresAt = numberValue(payload.expires_at) ?? now + 300;
        if (!token || requestedSessionId !== state.sessionId) {
          throw new Error("Secure widget session is unavailable. Please try again.");
        }
        return { token, expiresAt, sessionId: requestedSessionId };
      })();
      existingWidgetSessionPromise = bootstrapPromise;
      try {
        const credential = await bootstrapPromise;
        existingWidgetSessionCredential = credential;
        return credential.token;
      } finally {
        if (existingWidgetSessionPromise === bootstrapPromise) {
          existingWidgetSessionPromise = null;
        }
      }
    }

    async function existingWidgetJsonRequest(
      path: string,
      body: Record<string, unknown>,
      signal?: AbortSignal
    ): Promise<Record<string, unknown>> {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const token = await existingWidgetSessionToken(attempt > 0);
        if (signal?.aborted) {
          throw new DOMException("Request cancelled", "AbortError");
        }
        const response = await fetch(
          `${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/shopify-widget/${encodeURIComponent(
            options.customerId
          )}/${encodeURIComponent(options.appId)}${path}`,
          {
            method: "POST",
            credentials: "omit",
            signal,
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
        const payload = parseRecord(await response.json().catch(() => ({})));
        if (response.status === 401 && attempt === 0 && !signal?.aborted) {
          existingWidgetSessionCredential = null;
          continue;
        }
        if (!response.ok) {
          throw new Error(
            stringValue(payload.detail) || stringValue(payload.message) || `The request failed with ${response.status}.`
          );
        }
        return payload;
      }
      throw new Error("Secure widget authentication expired. Please try again.");
    }

    async function executePersonalizationMutation(pending: PendingConfirmation) {
      if (state.busy) {
        return;
      }
      state.busy = true;
      state.error = "";
      state.lastFailedContent = "";
      render();
      try {
        const mutation = personalizationMutationRequest(pending);
        const payload = await existingWidgetJsonRequest(mutation.path, {
          session_id: state.sessionId,
          context: shopperContextPayload(options),
          ...mutation.payload
        });
        const actions = normalizeActions(payload.runtime_actions);
        const content =
          stringValue(payload.message) ||
          stringValue(payload.summary) ||
          (pending.actionType === "preference_update"
            ? "Your saved preferences were updated."
            : pending.actionType === "preference_forget"
              ? "Your selected saved preference was removed."
              : "Personalization is turned off for you.");
        state.messages.push({
          role: "assistant",
          content,
          protocolVersion: "2",
          actions,
          trust: { grounded: true, source: "shopper-preference-profile" }
        });
        saveTranscript(options, state.sessionId, state.messages);
        dispatchActionDetail(pendingActionEvent(pending, "completed"), options);
      } catch (error) {
        state.error = error instanceof Error ? error.message : "The personalization change could not be completed.";
        dispatchActionDetail(pendingActionEvent(pending, "failed"), options);
      } finally {
        state.busy = false;
        pendingThreadScroll = "bottom";
        render();
      }
    }

    function personalizationMutationRequest(pending: PendingConfirmation): {
      path: string;
      payload: Record<string, unknown>;
    } {
      const mutation = pending.personalizationMutation;
      if (!mutation) {
        throw new Error("This personalization change is missing its authorized request details.");
      }
      const message = boundedPreferenceValue(mutation.message, 1000);
      const category = boundedPreferenceValue(mutation.category, 80);
      const value = boundedPreferenceValue(mutation.value, 160);
      const preferenceId = boundedPreferenceValue(mutation.preferenceId, 80);
      if (category && !/^[a-z][a-z0-9_-]{0,79}$/i.test(category)) {
        throw new Error("This preference category is invalid.");
      }
      if (preferenceId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(preferenceId)) {
        throw new Error("This saved preference identifier is invalid.");
      }
      if (pending.actionType === "preference_update") {
        if (!(category && value) && !message) {
          throw new Error("Choose a specific preference and value before updating it.");
        }
        if ((category && !value) || (!category && value)) {
          throw new Error("Both a preference category and value are required for this update.");
        }
        return { path: "/personalization/correct", payload: { message, category, value } };
      }
      if (pending.actionType === "preference_forget") {
        if (!preferenceId && !category && !mutation.forgetAll) {
          throw new Error("Choose a specific saved preference before forgetting it.");
        }
        return {
          path: "/personalization/forget",
          payload: {
            preference_id: preferenceId,
            category,
            value
          }
        };
      }
      if (pending.actionType === "preference_opt_out") {
        return { path: "/personalization/opt-out", payload: {} };
      }
      throw new Error("This personalization operation is not supported.");
    }

    function boundedPreferenceValue(value: string, maximumLength: number): string {
      return value.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, maximumLength);
    }

    function readFileAsDataUrl(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("The uploaded image could not be read."));
        reader.readAsDataURL(file);
      });
    }

    function updatePriceRange(input: HTMLInputElement) {
      const range = input.closest<HTMLElement>(".apw-price-range");
      const grid = range?.closest<HTMLElement>(".apw-product-grid");
      if (!range || !grid) {
        return;
      }
      const minInput = range.querySelector<HTMLInputElement>(".apw-price-min");
      const maxInput = range.querySelector<HTMLInputElement>(".apw-price-max");
      if (!minInput || !maxInput) {
        return;
      }
      let min = Number(minInput.value);
      let max = Number(maxInput.value);
      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return;
      }
      if (min > max) {
        if (input === minInput) {
          max = min;
          maxInput.value = String(max);
        } else {
          min = max;
          minInput.value = String(min);
        }
      }
      const cards = Array.from(grid.querySelectorAll<HTMLElement>(".apw-product-card-rich[data-price]"));
      let visible = 0;
      for (const card of cards) {
        const price = Number(card.getAttribute("data-price"));
        const hidden = Number.isFinite(price) && (price < min || price > max);
        card.toggleAttribute("data-filtered", hidden);
        if (!hidden) {
          visible += 1;
        }
      }
      const currency = range.getAttribute("data-currency") || "USD";
      const label = range.querySelector<HTMLElement>(".apw-price-range-label");
      if (label) {
        label.textContent = `${formatPrice(min, currency)} - ${formatPrice(max, currency)}`;
      }
      const count = grid.querySelector<HTMLElement>(".apw-product-count");
      if (count) {
        count.textContent = `${visible} visible`;
      }
      grid.toggleAttribute("data-filter-empty", visible === 0);
      const spread = Number(range.getAttribute("data-max") || 0) - Number(range.getAttribute("data-min") || 0);
      const left = spread > 0 ? ((min - Number(range.getAttribute("data-min"))) / spread) * 100 : 0;
      const right = spread > 0 ? 100 - ((max - Number(range.getAttribute("data-min"))) / spread) * 100 : 0;
      range.style.setProperty("--apw-price-left", `${Math.max(0, Math.min(100, left))}%`);
      range.style.setProperty("--apw-price-right", `${Math.max(0, Math.min(100, right))}%`);
    }

    function openConversation() {
      state.open = true; state.nudgeVisible = false; state.launcherInteracted = true;
      state.nudgeStopped = Boolean(state.config.nudge?.stopAfterOpen);
      window.clearTimeout(state.nudgeTimer); pendingThreadScroll = "preserve";
      render(); focusConversationEntry();
    }
    window.addEventListener("agent-platform:open-chat", openConversation);
    function updateTheme() {
      styleElement.textContent = styles(
        state.config.placement === "bottom-left" ? "left" : "right",
        options.theme ?? "auto",
        configPrimaryColor(state.config, options.primaryColor),
        stylePreset
      );
    }
    const themeObserver = new MutationObserver(updateTheme);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)");
    systemTheme.addEventListener("change", updateTheme);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", syncViewportMetrics);
    visualViewport?.addEventListener("scroll", syncViewportMetrics);
    window.addEventListener("resize", syncViewportMetrics);
    window.addEventListener("pagehide", handlePageHide);
    phoneViewport.addEventListener("change", syncViewportMetrics);
    syncViewportMetrics();
    render();
    void loadWidgetConfig(options)
      .then((config) => {
        if (!config) {
          return;
        }
        state.config = config;
        state.nudgeStopped = state.launcherInteracted && Boolean(config.nudge?.stopAfterOpen);
        scheduleNudge();
        render();
      })
      .catch((error) => {
        console.warn("Agent Platform widget config failed.", error);
      });
    scheduleNudge();
    return {
      host,
      get isOpen() {
        return state.open;
      },
      get sessionId() {
        return state.sessionId;
      },
      destroy: () => {
        window.clearTimeout(state.nudgeTimer);
        if (streamRenderFrame) {
          window.cancelAnimationFrame(streamRenderFrame);
          streamRenderFrame = 0;
        }
        visualViewport?.removeEventListener("resize", syncViewportMetrics);
        visualViewport?.removeEventListener("scroll", syncViewportMetrics);
        window.removeEventListener("resize", syncViewportMetrics);
        window.removeEventListener("pagehide", handlePageHide);
        window.removeEventListener("agent-platform:open-chat", openConversation);
        themeObserver.disconnect(); systemTheme.removeEventListener("change", updateTheme);
        phoneViewport.removeEventListener("change", syncViewportMetrics);
        syncPageScrollLock(true);
        stopVoice();
        closeFashionTryOn();
        host.remove();
      },
      open: openConversation,
      close: () => {
        savedThreadScrollTop = thread.scrollTop;
        if (state.voiceActive || state.voiceStarting) {
          stopVoice();
        }
        closeFashionTryOn();
        state.open = false;
        render();
        launcher.focus({ preventScroll: true });
        scheduleNudge();
      }
    };

    async function startVoice() {
      if (state.voiceActive || state.voiceStarting) {
        return;
      }
      const voiceConfig = normalizedVoiceConfig(state.config.voice);
      state.voiceError = "";
      state.lastVoiceTranscript = "";
      state.lastVoiceTranscriptAt = 0;
      if (!isVoiceAvailable(voiceConfig)) {
        state.voiceError = voiceConfig.vapiUnavailableReason || "Voice is not available for this app.";
        render();
        return;
      }
      const generation = voiceGeneration + 1;
      voiceGeneration = generation;
      voiceRequested = true;
      state.open = true;
      state.voiceActive = true;
      state.voiceStarting = true;
      state.voiceStatus = "connecting";
      render();
      try {
        if (voiceConfig.provider === "vapi") {
          await startVapiVoice(voiceConfig);
        } else if (voiceConfig.provider === "elevenlabs") {
          await startElevenLabsVoice(voiceConfig, generation);
        } else {
          await startGeminiVoice(voiceConfig, generation);
        }
        if (!voiceRequested || generation !== voiceGeneration) {
          return;
        }
        state.open = true;
        state.voiceActive = true;
      } catch (error) {
        if (!voiceRequested || generation !== voiceGeneration) {
          return;
        }
        voiceRequested = false;
        voiceGeneration += 1;
        state.voiceActive = false;
        state.voiceStatus = "error";
        state.voiceError = error instanceof Error ? error.message : "Voice failed to start.";
        teardownVoiceResources();
      } finally {
        state.voiceStarting = false;
        render();
      }
    }

    function stopVoice() {
      voiceRequested = false;
      voiceGeneration += 1;
      state.voiceActive = false;
      state.voiceStatus = "off";
      queuedVoiceTranscript = null;
      teardownVoiceResources();
    }

    function teardownVoiceResources() {
      window.clearTimeout(geminiReconnectTimer);
      geminiReconnectTimer = 0;
      window.clearTimeout(geminiReconnectResetTimer);
      geminiReconnectResetTimer = 0;
      geminiReconnectAttempts = 0;
      geminiResumeHandle = "";
      clearVoicePlayback();
      cleanupMicrophone();
      if (geminiSocket) {
        geminiSocket.close();
        geminiSocket = null;
      }
      if (vapiClient?.stop) {
        try {
          vapiClient.stop();
        } catch {
          // Vapi cleanup is best-effort.
        }
      }
    }

    async function startVapiVoice(voiceConfig: WidgetVoiceConfig) {
      if (!voiceConfig.vapiPublicKey || !voiceConfig.vapiAssistantId) {
        throw new Error(voiceConfig.vapiUnavailableReason || "Vapi assistant is not ready.");
      }
      const client = await ensureVapiClient(voiceConfig.vapiPublicKey);
      const assistantOverrides = {
        variableValues: {
          customer_id: options.customerId,
          app_id: options.appId,
          session_id: state.sessionId
        },
        metadata: {
          customer_id: options.customerId,
          app_id: options.appId,
          session_id: state.sessionId
        },
        transcriber: voiceConfig.transcriber && Object.keys(voiceConfig.transcriber).length ? voiceConfig.transcriber : undefined,
        firstMessage: voiceConfig.vapiFirstMessage,
        endCallPhrases: voiceConfig.vapiEndCallPhrases,
        silenceTimeoutSeconds: typeof voiceConfig.vapiSilenceTimeoutMs === "number" ? voiceConfig.vapiSilenceTimeoutMs / 1000 : undefined,
        maxDurationSeconds: typeof voiceConfig.vapiMaxDurationMs === "number" ? voiceConfig.vapiMaxDurationMs / 1000 : undefined
      };
      state.voiceStatus = "connecting";
      render();
      await client.start(voiceConfig.vapiAssistantId, assistantOverrides);
    }

    async function startElevenLabsVoice(voiceConfig: WidgetVoiceConfig, generation: number) {
      if (voiceConfig.websocketUrl) {
        await startRuntimeElevenLabsVoice(voiceConfig, generation);
        return;
      }
      if (!voiceConfig.elevenLabsSignedUrl) throw new Error(voiceConfig.elevenLabsUnavailableReason || "ElevenLabs agent is not ready.");
      await ensureVoiceAudioContext();
      const socket = new WebSocket(voiceConfig.elevenLabsSignedUrl);
      geminiSocket = socket;
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const finish = (callback: () => void) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          callback();
        };
        const timeout = window.setTimeout(() => finish(() => reject(new Error("ElevenLabs took too long to become ready."))), 30000);
        socket.addEventListener("open", () => {
          socket.send(JSON.stringify({
            type: "conversation_initiation_client_data",
            ...(voiceConfig.elevenLabsVoiceId
              ? { conversation_config_override: { tts: { voice_id: voiceConfig.elevenLabsVoiceId } } }
              : {})
          }));
        }, { once: true });
        socket.addEventListener("message", (message) => {
          const event = parseRecord(message.data);
          if (stringValue(event.type) === "conversation_initiation_metadata") {
            const metadata = parseRecord(event.conversation_initiation_metadata_event);
            voiceConfig.outputSampleRate = sampleRateFromElevenLabsFormat(stringValue(metadata.agent_output_audio_format), 16000);
            voiceConfig.inputSampleRate = sampleRateFromElevenLabsFormat(stringValue(metadata.user_input_audio_format), 16000);
            state.voiceStatus = "listening";
            finish(resolve);
          } else if (stringValue(event.type) === "error" || stringValue(event.type) === "client_error") {
            const providerMessage = stringValue(event.message || event.error) || "ElevenLabs rejected the conversation configuration.";
            const guidance = providerMessage.toLowerCase().includes("override")
              ? " Enable Voice ID overrides in the ElevenLabs agent Security settings."
              : "";
            finish(() => reject(new Error(`${providerMessage}${guidance}`)));
          }
        });
        socket.addEventListener("error", () => finish(() => reject(new Error("ElevenLabs voice could not start."))), { once: true });
        socket.addEventListener("close", () => finish(() => reject(new Error("ElevenLabs closed before the agent became ready."))), { once: true });
      });
      socket.addEventListener("message", (message) => handleElevenLabsEvent(message.data, voiceConfig));
      await startMicrophone(voiceConfig, generation);
    }

    async function startRuntimeElevenLabsVoice(voiceConfig: WidgetVoiceConfig, generation: number) {
      await ensureVoiceAudioContext();
      state.voiceStatus = "connecting";
      render();
      const socket = new WebSocket(voiceConfig.websocketUrl as string);
      socket.binaryType = "arraybuffer";
      geminiSocket = socket;
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const settle = (callback: () => void) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          callback();
        };
        const timeout = window.setTimeout(
          () => settle(() => reject(new Error("ElevenLabs took too long to become ready."))),
          20000
        );
        socket.addEventListener("open", () => {
          socket.send(JSON.stringify({ type: "auth", token: options.runtimeKey, session_id: state.sessionId }));
        }, { once: true });
        const setupListener = (message: MessageEvent) => {
          const event = parseRecord(message.data);
          if (stringValue(event.type) === "status" && stringValue(event.status) === "listening") {
            socket.removeEventListener("message", setupListener);
            state.voiceStatus = "listening";
            settle(resolve);
          } else if (stringValue(event.type) === "error") {
            socket.removeEventListener("message", setupListener);
            settle(() => reject(new Error(stringValue(event.message) || "ElevenLabs voice setup failed.")));
          }
        };
        socket.addEventListener("message", setupListener);
        socket.addEventListener("error", () => settle(() => reject(new Error("ElevenLabs voice could not start."))), { once: true });
        socket.addEventListener("close", () => settle(() => reject(new Error("ElevenLabs closed before becoming ready."))), { once: true });
      });
      socket.addEventListener("message", (message) => handleGeminiEvent(message.data, voiceConfig));
      socket.addEventListener("close", () => {
        if (geminiSocket === socket && state.voiceActive) {
          geminiSocket = null;
          voiceRequested = false;
          voiceGeneration += 1;
          queuedVoiceTranscript = null;
          state.voiceActive = false;
          state.voiceStatus = "error";
          state.voiceError = "Voice disconnected. Start voice again when ready.";
          cleanupMicrophone();
          render();
        }
      });
      await startMicrophone(voiceConfig, generation);
    }

    function handleElevenLabsEvent(raw: unknown, voiceConfig: WidgetVoiceConfig) {
      if (!voiceRequested) {
        return;
      }
      const event = parseRecord(raw);
      const type = stringValue(event.type);
      if (type === "audio") {
        const audio = parseRecord(event.audio_event);
        const data = stringValue(audio.audio_base_64);
        if (data) void playPcm16Audio(data, voiceConfig.outputSampleRate ?? 16000);
        state.voiceStatus = "speaking";
      } else if (type === "user_transcript") {
        const transcript = parseRecord(event.user_transcription_event);
        const text = stringValue(transcript.user_transcript);
        if (text) state.lastVoiceTranscript = text;
      } else if (type === "interruption") {
        clearVoicePlayback();
        state.voiceStatus = "listening";
      } else if (type === "ping") {
        const ping = parseRecord(event.ping_event);
        geminiSocket?.send(JSON.stringify({ type: "pong", event_id: ping.event_id }));
      } else if (type === "error" || type === "client_error") {
        const providerMessage = stringValue(event.message || event.error) || "ElevenLabs voice encountered an error.";
        state.voiceError = providerMessage.toLowerCase().includes("override")
          ? `${providerMessage} Enable Voice ID overrides in the ElevenLabs agent Security settings.`
          : providerMessage;
        state.voiceStatus = "error";
      }
      render();
    }

    async function ensureVapiClient(publicKey: string): Promise<any> {
      if (!vapiClient) {
        const sdkUrl = "https://esm.sh/@vapi-ai/web@2.5.2?bundle";
        const module = await import(/* webpackIgnore: true */ sdkUrl);
        const Vapi = (module as any).default || (module as any).Vapi;
        vapiClient = new Vapi(publicKey);
      }
      if (!vapiBound) {
        vapiBound = true;
        vapiClient.on?.("call-start", () => {
          if (!voiceRequested) {
            try {
              vapiClient?.stop?.();
            } catch {
              // A cancelled Vapi start may finish after the user has stopped voice.
            }
            return;
          }
          state.voiceActive = true;
          state.voiceStatus = "listening";
          render();
        });
        vapiClient.on?.("call-end", () => {
          voiceRequested = false;
          voiceGeneration += 1;
          queuedVoiceTranscript = null;
          state.voiceActive = false;
          if (state.voiceStatus !== "error") {
            state.voiceStatus = "off";
          }
          render();
        });
        vapiClient.on?.("speech-start", () => {
          state.voiceStatus = "speaking";
          render();
        });
        vapiClient.on?.("speech-end", () => {
          state.voiceStatus = "listening";
          render();
        });
        vapiClient.on?.("error", (error: unknown) => {
          if (!voiceRequested) {
            return;
          }
          voiceRequested = false;
          voiceGeneration += 1;
          queuedVoiceTranscript = null;
          state.voiceActive = false;
          state.voiceError = error instanceof Error ? error.message : "Vapi voice error.";
          state.voiceStatus = "error";
          render();
        });
        vapiClient.on?.("message", (message: unknown) => {
          const transcript = vapiTranscript(message);
          if (transcript) {
            void handleVoiceTranscript(transcript, "vapi");
          }
        });
      }
      return vapiClient;
    }

    async function startGeminiVoice(voiceConfig: WidgetVoiceConfig, generation: number) {
      if (!voiceConfig.websocketUrl) {
        throw new Error("Gemini Live websocket is not configured.");
      }
      const playbackReady = ensureVoiceAudioContext();
      state.voiceStatus = "connecting";
      render();
      geminiReconnectAttempts = 0;
      geminiResumeHandle = "";
      await connectGeminiVoice(voiceConfig);
      if (!voiceRequested || generation !== voiceGeneration) {
        return;
      }
      await playbackReady;
      if (!voiceRequested || generation !== voiceGeneration) {
        return;
      }
      await startMicrophone(voiceConfig, generation);
    }

    async function ensureVoiceAudioContext() {
      const context = audioContext && audioContext.state !== "closed" ? audioContext : new AudioContext();
      audioContext = context;
      if (context.state !== "running") {
        await context.resume();
      }
      if (context.state !== "running") {
        throw new Error("Browser audio is paused. Start voice again to enable sound.");
      }
      return context;
    }

    async function connectGeminiVoice(voiceConfig: WidgetVoiceConfig) {
      if (!voiceConfig.websocketUrl) {
        throw new Error("Gemini Live websocket is not configured.");
      }
      const url = new URL(voiceConfig.websocketUrl);
      const socket = new WebSocket(url.toString());
      socket.binaryType = "arraybuffer";
      geminiSocket = socket;
      try {
        await new Promise<void>((resolve, reject) => {
          let settled = false;
          const settle = (callback: () => void) => {
            if (settled) {
              return;
            }
            settled = true;
            window.clearTimeout(timeout);
            callback();
          };
          const timeout = window.setTimeout(() => {
            settle(() => reject(new Error("Voice took too long to become ready. Try again or use text chat.")));
          }, 30000);
          socket.addEventListener(
            "open",
            () => {
              socket.send(
                JSON.stringify({
                  type: "auth",
                  token: options.runtimeKey,
                  session_id: state.sessionId,
                  setup: {
                    type: "setup",
                    model: voiceConfig.model,
                    api_version: voiceConfig.apiVersion,
                    voice_name: voiceConfig.voice,
                    language_code: voiceConfig.language,
                    interaction_mode: voiceConfig.interactionMode,
                    input_transcription_enabled: voiceConfig.inputTranscriptionEnabled,
                    output_transcription_enabled: voiceConfig.outputTranscriptionEnabled,
                    session_resumption_handle: voiceConfig.sessionResumptionEnabled ? geminiResumeHandle : ""
                  }
                })
              );
            },
            { once: true }
          );
          const setupListener = (event: MessageEvent) => {
            const payload = parseRecord(event.data);
            const type = stringValue(payload.type);
            const status = stringValue(payload.status);
            if (type === "status") {
              if (status === "ready" || status === "listening") {
                state.voiceError = "";
              }
              state.voiceStatus = normalizeVoiceStatus(status);
              render();
              if (status === "listening") {
                socket.removeEventListener("message", setupListener);
                settle(resolve);
              }
              return;
            }
            if (type === "error") {
              socket.removeEventListener("message", setupListener);
              settle(() => reject(new Error(stringValue(payload.message) || "Voice setup failed.")));
            }
          };
          socket.addEventListener("message", setupListener);
          socket.addEventListener(
            "error",
            () => settle(() => reject(new Error("Voice could not start. Check the runtime URL and voice settings."))),
            { once: true }
          );
          socket.addEventListener(
            "close",
            (event) => {
              socket.removeEventListener("message", setupListener);
              settle(() => reject(new Error(geminiCloseMessage(event))));
            },
            { once: true }
          );
        });
      } catch (error) {
        if (geminiSocket === socket) {
          geminiSocket = null;
        }
        socket.close();
        throw error;
      }
      socket.addEventListener("message", (event) => handleGeminiEvent(event.data, voiceConfig));
      socket.addEventListener("close", (event) => {
        if (geminiSocket === socket && state.voiceActive) {
          geminiSocket = null;
          scheduleGeminiReconnect(voiceConfig, geminiCloseMessage(event));
        }
      });
      window.clearTimeout(geminiReconnectResetTimer);
      geminiReconnectResetTimer = window.setTimeout(() => {
        geminiReconnectAttempts = 0;
        geminiReconnectResetTimer = 0;
      }, 10_000);
    }

    function scheduleGeminiReconnect(voiceConfig: WidgetVoiceConfig, reason: string) {
      if (!state.voiceActive || geminiReconnectAttempts >= 2) {
        voiceRequested = false;
        voiceGeneration += 1;
        state.voiceActive = false;
        state.voiceStatus = "error";
        state.voiceError = reason;
        cleanupMicrophone();
        render();
        return;
      }
      geminiReconnectAttempts += 1;
      state.voiceStatus = "connecting";
      state.voiceError = "Reconnecting voice...";
      render();
      const delay = geminiReconnectAttempts === 1 ? 250 : 750;
      window.clearTimeout(geminiReconnectTimer);
      geminiReconnectTimer = window.setTimeout(() => {
        void connectGeminiVoice(voiceConfig).catch((error) => {
          scheduleGeminiReconnect(
            voiceConfig,
            error instanceof Error ? error.message : "Voice could not reconnect."
          );
        });
      }, delay);
    }

    async function startMicrophone(voiceConfig: WidgetVoiceConfig, generation: number) {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser does not support microphone capture.");
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        if (!voiceRequested || generation !== voiceGeneration) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        mediaStream = stream;
      } catch (error) {
        if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
          throw new Error("Microphone access was blocked. Allow microphone access and try again.");
        }
        throw new Error("Microphone could not be started. Check your browser audio settings.");
      }
      audioContext = await ensureVoiceAudioContext();
      if (!voiceRequested || generation !== voiceGeneration) {
        cleanupMicrophone();
        return;
      }
      if (!audioContext.audioWorklet) {
        throw new Error("This browser does not support high-quality real-time audio. Use text chat or update the browser.");
      }
      audioSource = audioContext.createMediaStreamSource(mediaStream);
      const workletSource = `
        class AgentPlatformVoiceCapture extends AudioWorkletProcessor {
          constructor() {
            super();
            this.buffer = new Float32Array(2048);
            this.offset = 0;
          }
          process(inputs) {
            const channel = inputs[0] && inputs[0][0];
            if (!channel) return true;
            let index = 0;
            while (index < channel.length) {
              const count = Math.min(channel.length - index, this.buffer.length - this.offset);
              this.buffer.set(channel.subarray(index, index + count), this.offset);
              this.offset += count;
              index += count;
              if (this.offset === this.buffer.length) {
                const ready = this.buffer;
                this.port.postMessage(ready, [ready.buffer]);
                this.buffer = new Float32Array(2048);
                this.offset = 0;
              }
            }
            return true;
          }
        }
        registerProcessor("agent-platform-voice-capture", AgentPlatformVoiceCapture);
      `;
      const workletUrl = URL.createObjectURL(new Blob([workletSource], { type: "text/javascript" }));
      try {
        await audioContext.audioWorklet.addModule(workletUrl);
      } finally {
        URL.revokeObjectURL(workletUrl);
      }
      if (!voiceRequested || generation !== voiceGeneration) {
        cleanupMicrophone();
        return;
      }
      audioProcessor = new AudioWorkletNode(audioContext, "agent-platform-voice-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1]
      });
      audioProcessor.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (!geminiSocket || geminiSocket.readyState !== WebSocket.OPEN || !state.voiceActive) {
          return;
        }
        if (state.voiceStatus === "speaking" && voiceConfig.bargeInEnabled === false) {
          return;
        }
        const samples = event.data instanceof Float32Array ? event.data : new Float32Array(event.data);
        const pcm = floatToPcm16(samples, audioContext?.sampleRate ?? 48000, voiceConfig.inputSampleRate ?? 16000);
        if (!pcm.byteLength) {
          return;
        }
        if (voiceConfig.provider === "elevenlabs") {
          const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
          let binary = "";
          for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
          geminiSocket.send(JSON.stringify(
            voiceConfig.websocketUrl
              ? { type: "audio", data: btoa(binary) }
              : { user_audio_chunk: btoa(binary) }
          ));
        } else {
          geminiSocket.send(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength));
        }
      };
      audioSource.connect(audioProcessor);
      audioSilenceGain = audioContext.createGain();
      audioSilenceGain.gain.value = 0;
      audioProcessor.connect(audioSilenceGain);
      audioSilenceGain.connect(audioContext.destination);
    }

    function cleanupMicrophone() {
      if (audioProcessor) {
        audioProcessor.disconnect();
        audioProcessor.port.onmessage = null;
        audioProcessor = null;
      }
      if (audioSilenceGain) {
        audioSilenceGain.disconnect();
        audioSilenceGain = null;
      }
      if (audioSource) {
        audioSource.disconnect();
        audioSource = null;
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        mediaStream = null;
      }
      if (audioContext) {
        void audioContext.close();
        audioContext = null;
      }
      window.clearTimeout(voicePlaybackTimer);
      voicePlaybackTimer = 0;
      audioQueueTime = 0;
    }

    function handleGeminiEvent(raw: unknown, voiceConfig: WidgetVoiceConfig) {
      if (!voiceRequested) {
        return;
      }
      if (raw instanceof ArrayBuffer) {
        if (raw.byteLength) {
          state.voiceStatus = "speaking";
          render();
          void playPcm16Bytes(new Uint8Array(raw), voiceConfig.outputSampleRate ?? 24000);
        }
        return;
      }
      const event = parseRecord(raw);
      const eventType = stringValue(event.type);
      if (eventType === "session_resumption") {
        geminiResumeHandle = stringValue(event.handle);
        return;
      }
      if (eventType === "go_away") {
        if (geminiSocket && state.voiceActive) {
          const expiringSocket = geminiSocket;
          geminiSocket = null;
          expiringSocket.close(1000, "Session resumption");
          scheduleGeminiReconnect(voiceConfig, "Voice session expired before it could resume.");
        }
        return;
      }
      if (eventType === "playback_interrupted") {
        clearVoicePlayback();
        state.voiceStatus = "listening";
        render();
        return;
      }
      if (eventType === "status") {
        const providerStatus = stringValue(event.status);
        const providerError = voiceProviderStatusError(providerStatus);
        if (providerError) {
          state.voiceError = providerError;
          state.voiceStatus = "error";
        } else {
          if (providerStatus === "ready" || providerStatus === "listening" || providerStatus === "user_speaking") {
            state.voiceError = "";
          }
          state.voiceStatus = normalizeVoiceStatus(providerStatus);
        }
        if (state.voiceStatus === "turn_complete") {
          const now = audioContext?.currentTime ?? 0;
          state.voiceStatus = audioQueueTime > now + 0.05 ? "speaking" : "listening";
        } else if (state.voiceStatus === "listening" && audioQueueTime > (audioContext?.currentTime ?? 0) + 0.05) {
          state.voiceStatus = "speaking";
        }
        render();
        return;
      }
      if (eventType === "error") {
        state.voiceError = stringValue(event.message) || "Gemini Live error.";
        state.voiceStatus = "error";
        render();
        return;
      }
      if (eventType === "transcript" && event.role === "user" && event.final === true) {
        const text = stringValue(event.text);
        if (text) {
          clearVoicePlayback();
          const provider = voiceConfig.provider === "openai-realtime"
            ? "openai-realtime"
            : voiceConfig.provider === "elevenlabs"
              ? "elevenlabs"
              : "gemini-live";
          void handleVoiceTranscript(text, provider);
        }
        return;
      }
      if (eventType === "audio") {
        const data = stringValue(event.data);
        if (data) {
          state.voiceStatus = "speaking";
          render();
          const mimeRate = Number(stringValue(event.mime_type).match(/rate=(\d+)/)?.[1] || 0);
          void playPcm16Audio(data, mimeRate || voiceConfig.outputSampleRate || 24000);
        }
      }
    }

    function geminiCloseMessage(event: CloseEvent): string {
      const reason = event.reason.trim();
      if (reason) {
        return `Voice disconnected: ${reason}`;
      }
      if (event.code && event.code !== 1000 && event.code !== 1005) {
        return `Voice disconnected with code ${event.code}. Start voice again when ready.`;
      }
      return "Voice disconnected. Start voice again when ready.";
    }

    async function handleVoiceTranscript(text: string, provider: "gemini-live" | "openai-realtime" | "elevenlabs" | "vapi") {
      const normalized = text.trim();
      const dedupeKey = normalized.toLowerCase().replace(/\s+/g, " ");
      const now = Date.now();
      if (!normalized || (dedupeKey === state.lastVoiceTranscript && now - state.lastVoiceTranscriptAt < 2_000)) {
        return;
      }
      state.lastVoiceTranscript = dedupeKey;
      state.lastVoiceTranscriptAt = now;
      if (state.busy) {
        queuedVoiceTranscript = { text: normalized, provider };
        return;
      }
      await runVoiceTranscript(normalized, provider);
    }

    async function runVoiceTranscript(normalized: string, provider: "gemini-live" | "openai-realtime" | "elevenlabs" | "vapi") {
      const turnGeneration = voiceGeneration;
      const intakeResolution = resolveActiveInsuranceTurn(normalized);
      if (intakeResolution?.kind === "ambiguous") {
        appendInsuranceClarification(normalized, intakeResolution.message);
        const speechRequested = voiceRequested && turnGeneration === voiceGeneration
          ? speakRuntimeAnswer(intakeResolution.message, provider)
          : false;
        state.voiceStatus = speechRequested ? "speaking" : "listening";
        render();
        return;
      }
      const runtimeTranscript = intakeResolution?.kind === "resolved" ? intakeResolution.prompt : normalized;
      const displayTranscript = intakeResolution?.kind === "resolved" ? intakeResolution.displayContent : normalized;
      state.voiceStatus = "thinking";
      state.messages.push({ role: "user", content: displayTranscript });
      state.pendingChoice = displayTranscript;
      state.busy = true;
      saveTranscript(options, state.sessionId, state.messages);
      pendingThreadScroll = "bottom";
      render();
      try {
        const response = await fetch(`${trimTrailingSlash(options.runtimeUrl ?? defaultRuntimeUrl)}/v1/voice/turn`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${options.runtimeKey}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            customer_id: options.customerId,
            app_id: options.appId,
            session_id: state.sessionId,
            transcript: runtimeTranscript,
            shopper_context: shopperContextPayload(options),
            metadata: { provider }
          })
        });
        if (!response.ok) {
          throw new Error(await responseMessage(response));
        }
        const payload = (await response.json()) as RuntimeChatResponse;
        state.messages.push(chatMessageFromPayload(payload));
        saveTranscript(options, state.sessionId, state.messages);
        const speechRequested = voiceRequested && turnGeneration === voiceGeneration
          ? speakRuntimeAnswer(voiceSpeechText(payload), provider)
          : false;
        if (!speechRequested && state.voiceActive) {
          state.voiceStatus = "listening";
        }
      } catch (error) {
        if (voiceRequested && turnGeneration === voiceGeneration) {
          state.voiceError = error instanceof Error ? error.message : "Voice request failed.";
          state.voiceStatus = "error";
        }
      } finally {
        state.busy = false;
        state.pendingChoice = "";
        const latestMessage = state.messages[state.messages.length - 1];
        const hasActiveRichAction =
          latestMessage?.role === "assistant" &&
          normalizeActions(latestMessage.actions).some((action) => ["intake_form", "coverage_card"].includes(action.type));
        pendingThreadScroll = hasActiveRichAction ? "active" : "bottom";
        render();
        const queued = queuedVoiceTranscript;
        queuedVoiceTranscript = null;
        if (queued && voiceRequested && turnGeneration === voiceGeneration) {
          void runVoiceTranscript(queued.text, queued.provider);
        }
      }
    }

    function speakRuntimeAnswer(text: string, provider: "gemini-live" | "openai-realtime" | "elevenlabs" | "vapi"): boolean {
      if (!text.trim()) {
        return false;
      }
      if (
        (provider === "gemini-live" || provider === "openai-realtime" || provider === "elevenlabs")
        && geminiSocket?.readyState === WebSocket.OPEN
      ) {
        geminiSocket.send(JSON.stringify({ type: "speak", text }));
        return true;
      }
      if (provider === "vapi" && vapiClient) {
        if (typeof vapiClient.say === "function") {
          vapiClient.say(text, true);
        } else if (typeof vapiClient.send === "function") {
          vapiClient.send({ type: "say", message: text });
        }
        return true;
      }
      return false;
    }

    async function playPcm16Audio(base64Audio: string, sampleRate: number) {
      return playPcm16Bytes(base64ToBytes(base64Audio), sampleRate);
    }

    async function playPcm16Bytes(bytes: Uint8Array, sampleRate: number) {
      const context = await ensureVoiceAudioContext();
      if (!voiceRequested || !state.voiceActive) {
        return;
      }
      const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
      const samples = new Int16Array(Math.floor(bytes.byteLength / 2));
      for (let index = 0; index < samples.length; index += 1) {
        samples[index] = view.getInt16(index * 2, true);
      }
      const buffer = context.createBuffer(1, samples.length, sampleRate);
      const channel = buffer.getChannelData(0);
      for (let index = 0; index < samples.length; index += 1) {
        channel[index] = Math.max(-1, Math.min(1, samples[index] / 32768));
      }
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(context.destination);
      activePlaybackSources.add(source);
      source.onended = () => activePlaybackSources.delete(source);
      const startAt = Math.max(context.currentTime, audioQueueTime);
      source.start(startAt);
      audioQueueTime = startAt + buffer.duration;
      state.voiceStatus = "speaking";
      render();
      window.clearTimeout(voicePlaybackTimer);
      voicePlaybackTimer = window.setTimeout(() => {
        if (state.voiceActive && state.voiceStatus === "speaking") {
          state.voiceStatus = "listening";
          render();
        }
      }, Math.max(150, Math.ceil((audioQueueTime - context.currentTime) * 1000) + 100));
    }

    function clearVoicePlayback() {
      for (const source of activePlaybackSources) {
        try {
          source.stop();
        } catch {
          // A source may already have ended.
        }
        source.disconnect();
      }
      activePlaybackSources.clear();
      window.clearTimeout(voicePlaybackTimer);
      voicePlaybackTimer = 0;
      audioQueueTime = audioContext?.currentTime ?? 0;
    }

    function voiceSpeechText(payload: RuntimeChatResponse): string {
      const speech = payload.speech && typeof payload.speech === "object" ? payload.speech : null;
      const approved = stringValue(speech?.content) || stringValue(speech?.text);
      return publicVisitorText(approved || payload.message.content);
    }

    function scheduleNudge() {
      window.clearTimeout(state.nudgeTimer);
      const nudge = state.config.nudge;
      if (!nudge?.enabled || state.open || state.nudgeStopped) {
        state.nudgeVisible = false;
        return;
      }
      if (nudge.oncePerPage && state.nudgeStarted) {
        state.nudgeVisible = false;
        return;
      }
      state.nudgeTimer = window.setTimeout(() => {
        state.nudgeStarted = true;
        state.nudgeVisible = true;
        render();
      }, Math.max(0, Number(nudge.delayMs ?? 5000)));
    }
  }

  function styles(position: "right" | "left", theme: "auto" | "light" | "dark", _primaryColor?: string, experience?: WidgetStylePreset): string {
    // The public website owns its light brand theme; other embeds retain platform theming.
    if (experience === "sotm") return platformWidgetStyles.replaceAll("__SIDE__", position) + sotmWidgetStyles;
    const pageTheme = document.documentElement.dataset.theme ?? (document.documentElement.classList.contains("dark") ? "dark" : undefined);
    const mode = pageTheme === "dark" || pageTheme === "light" ? pageTheme : theme === "auto" ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : theme;
    return platformWidgetStyles.replaceAll("__SIDE__", position) + (mode === "dark" ? platformWidgetDarkTokens : "");
  }

  function normalizedColor(value?: string): string {
    return value && /^#[0-9a-fA-F]{3,8}$/.test(value.trim()) ? value.trim() : "#2563eb";
  }

  (window as Window & { AgentPlatformWidget?: AgentPlatformWidgetApi }).AgentPlatformWidget = {
    mount: mountWidget
  };

  const currentScript = document.currentScript;
  if (currentScript instanceof HTMLScriptElement && !currentScript.hasAttribute(widgetAttribute)) {
    currentScript.setAttribute(widgetAttribute, "true");
    const options = scriptOptions(currentScript);
    if (options) {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => autoMountWidget(options, currentScript), { once: true });
      } else {
        autoMountWidget(options, currentScript);
      }
    }
  }
})();
